import { FC, useEffect } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Switch,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createPpwrPackagingTypeRequest,
	resetSavePpwrPackagingTypeSuccess,
	updatePpwrPackagingTypeRequest,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrPackagingTypeOptions,
	getPpwrPackagingTypeOptionsLoading,
	getPpwrPackagingTypeSaveLoading,
	getPpwrPackagingTypeSaveSuccess,
} from 'redux/reducers/ppwr/selectors';
import { TPackagingTypeDialogProps, TPackagingTypeFormValues } from './types';

const validationSchema = Yup.object({
	pckgTypeName: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
	pckgTypeHandler: Yup.string().max(255, 'Max 255 characters'),
	examples: Yup.string(),
	jotformProductPackHandler: Yup.string().max(255, 'Max 255 characters'),
	proofRequired: Yup.boolean().required(),
	defaultPckgTypeId: Yup.number().nullable(),
});

export const PackagingTypeDialog: FC<TPackagingTypeDialogProps> = ({
	open,
	onClose,
	packagingTypeToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!packagingTypeToEdit;

	const loading = useSelector(getPpwrPackagingTypeSaveLoading);
	const saveSuccess = useSelector(getPpwrPackagingTypeSaveSuccess);
	const options = useSelector(getPpwrPackagingTypeOptions);
	const optionsLoading = useSelector(getPpwrPackagingTypeOptionsLoading);

	const formik = useFormik<TPackagingTypeFormValues>({
		initialValues: {
			pckgTypeName: packagingTypeToEdit?.pckgTypeName ?? '',
			pckgTypeHandler: packagingTypeToEdit?.pckgTypeHandler ?? '',
			examples: packagingTypeToEdit?.examples ?? '',
			jotformProductPackHandler: packagingTypeToEdit?.jotformProductPackHandler ?? '',
			proofRequired: packagingTypeToEdit?.proofRequired ?? false,
			defaultPckgTypeId: packagingTypeToEdit?.defaultPckgTypeId ?? null,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				pckgTypeName: values.pckgTypeName,
				examples: values.examples || null,
				jotformProductPackHandler: values.jotformProductPackHandler || null,
				proofRequired: values.proofRequired,
				defaultPckgTypeId: values.defaultPckgTypeId,
			};
			if (isEdit && packagingTypeToEdit) {
				const pckgTypeHandler = values.pckgTypeHandler || null;
				// The handler is saved through its own endpoint, so it is sent only when it changed.
				const handler =
					pckgTypeHandler !== (packagingTypeToEdit.pckgTypeHandler || null)
						? { pckgTypeHandler }
						: undefined;
				dispatch(updatePpwrPackagingTypeRequest({ id: packagingTypeToEdit.id, body, handler }));
			} else {
				dispatch(createPpwrPackagingTypeRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSavePpwrPackagingTypeSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	// A packaging type cannot be its own default.
	const defaultOptions = (options ?? []).filter((option) => option.id !== packagingTypeToEdit?.id);
	const selectedDefault = defaultOptions.find(
		(option) => option.id === formik.values.defaultPckgTypeId,
	);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit Packaging Type' : 'Add New Packaging Type'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="pckgTypeName"
						size="small"
						value={formik.values.pckgTypeName}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.pckgTypeName && !!formik.errors.pckgTypeName}
						helperText={formik.touched.pckgTypeName && formik.errors.pckgTypeName}
					/>
					{isEdit && (
						<TextField
							label="Handler"
							name="pckgTypeHandler"
							size="small"
							value={formik.values.pckgTypeHandler}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.pckgTypeHandler && !!formik.errors.pckgTypeHandler}
							helperText={formik.touched.pckgTypeHandler && formik.errors.pckgTypeHandler}
						/>
					)}
					<TextField
						label="Examples"
						name="examples"
						size="small"
						multiline
						minRows={2}
						value={formik.values.examples}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.examples && !!formik.errors.examples}
						helperText={formik.touched.examples && formik.errors.examples}
					/>
					<TextField
						label="Jotform Product Pack Handler"
						name="jotformProductPackHandler"
						size="small"
						value={formik.values.jotformProductPackHandler}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.jotformProductPackHandler && !!formik.errors.jotformProductPackHandler
						}
						helperText={
							formik.touched.jotformProductPackHandler && formik.errors.jotformProductPackHandler
						}
					/>
					<Autocomplete
						options={defaultOptions}
						value={selectedDefault ?? null}
						loading={optionsLoading}
						onChange={(event, option) =>
							formik.setFieldValue('defaultPckgTypeId', option?.id ?? null)
						}
						getOptionLabel={(option) => option.pckgTypeName ?? String(option.id)}
						isOptionEqualToValue={(option, selected) => option.id === selected.id}
						size="small"
						renderInput={(params) => (
							<TextField {...params} label="Default Packaging Type" name="defaultPckgTypeId" />
						)}
					/>
					<FormControlLabel
						control={
							<Switch
								checked={formik.values.proofRequired}
								onChange={(e) => {
									void formik.setFieldValue('proofRequired', e.target.checked);
								}}
								name="proofRequired"
							/>
						}
						label="Proof Required"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
